/**
 * TopperSelect — Topper vergelijking (Horizon theme)
 * Custom element: init per section-instance, automatische cleanup via AbortController.
 * - vorige/volgende carouselknoppen + disabled states (mobiel)
 * - tap-vs-swipe detectie voor volledig klikbare kaarten
 * - theme editor: geselecteerd blok in beeld scrollen
 * Werkt zonder JS: native scroll/scroll-snap blijft bruikbaar.
 */
const SWIPE_THRESHOLD = 10;

class TopperComparison extends HTMLElement {
  #abort = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.grid = this.querySelector('[data-tc-grid]');
    this.prevBtn = this.querySelector('[data-tc-prev]');
    this.nextBtn = this.querySelector('[data-tc-next]');

    if (!this.grid) return;

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.#scrollByCard(-1), { signal });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.#scrollByCard(1), { signal });
    }

    this.grid.addEventListener('scroll', () => this.#updateArrows(), { passive: true, signal });
    window.addEventListener('resize', () => this.#updateArrows(), { signal });

    this.#bindCards(signal);
    this.#updateArrows();

    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener(
        'shopify:block:select',
        (event) => {
          if (this.contains(event.target)) {
            event.target.scrollIntoView({ behavior: this.#motion(), inline: 'center', block: 'nearest' });
          }
        },
        { signal }
      );
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  #motion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  #cardWidth() {
    const card = this.grid.querySelector('[data-tc-card]');
    if (!card) return this.grid.clientWidth * 0.86;
    const styles = getComputedStyle(this.grid);
    const gap = parseFloat(styles.columnGap || styles.gap) || 16;
    return card.getBoundingClientRect().width + gap;
  }

  #scrollByCard(direction) {
    this.grid.scrollBy({ left: direction * this.#cardWidth(), behavior: this.#motion() });
  }

  #updateArrows() {
    if (!this.prevBtn && !this.nextBtn) return;
    const el = this.grid;
    const atStart = el.scrollLeft <= 4;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
    if (this.prevBtn) this.prevBtn.disabled = atStart;
    if (this.nextBtn) this.nextBtn.disabled = atEnd;
  }

  /**
   * Kaarten volledig klikbaar maken als pointer-gemak.
   * Geen tabindex/role: de CTA-link in de kaart is de toegankelijke link,
   * dus toetsenbord en screenreaders hebben al een volwaardig alternatief.
   */
  #bindCards(signal) {
    const cards = this.grid.querySelectorAll('[data-tc-card][data-clickable="true"]');

    cards.forEach((card) => {
      const url = card.getAttribute('data-card-url');
      if (!url) return;

      let startX = 0;
      let startY = 0;
      let swiped = false;
      let down = false;

      const isInteractive = (target) =>
        target.closest('a, button, input, textarea, select, label, [role="button"]');

      card.addEventListener(
        'pointerdown',
        (event) => {
          down = true;
          swiped = false;
          startX = event.clientX;
          startY = event.clientY;
        },
        { signal }
      );

      card.addEventListener(
        'pointermove',
        (event) => {
          if (!down) return;
          if (
            Math.abs(event.clientX - startX) > SWIPE_THRESHOLD ||
            Math.abs(event.clientY - startY) > SWIPE_THRESHOLD
          ) {
            swiped = true;
          }
        },
        { signal }
      );

      const onUp = () => {
        down = false;
      };
      card.addEventListener('pointerup', onUp, { signal });
      card.addEventListener('pointercancel', onUp, { signal });

      card.addEventListener(
        'click',
        (event) => {
          if (swiped) {
            event.preventDefault();
            return;
          }
          if (isInteractive(event.target)) return; // eigen CTA / links regelen zichzelf
          window.location.href = url;
        },
        { signal }
      );
    });
  }
}

if (!customElements.get('topper-comparison')) {
  customElements.define('topper-comparison', TopperComparison);
}
